import { gql } from "@apollo/client";
import { MEDIA_ITEM_FRAGMENT } from "./MediaItemFragment";
import { EVENT_FEED_FRAGMENT } from "./EventFeedFragment";

export const RESOURCE_FLEXIBLE_CONTENT_FRAGMENT = gql`
  fragment ResourceFlexibleContentFragment on Resource_Flexiblecontent {
    blocks {
      ... on Resource_Flexiblecontent_Blocks_Hero {
        fieldGroupName
        hasCtas
        variant
        title
        subtitle
        content
        ctas {
          link {
            target
            title
            url
          }
          type
        }
        oneTime {
          title
          oneTimeOptions {
            id
            label
          }
        }
        monthly {
          monthlyOptions {
            id
            label
          }
          title
        }
        rightGraphic
        leftGraphic
        heroIcon {
          ...MediaItemFragment
        }
        featuredImage {
          ...MediaItemFragment
        }
        featuredImageMobile {
          ...MediaItemFragment
        }
      }
      ... on Resource_Flexiblecontent_Blocks_TextImage {
        fieldGroupName
        textImageRepeater {
          content
          title
          pawsIconPostion
          imageSide
          hasSidePawsIcon
          hasCtas
          backgroundColor
          sectionId
          ctaRepeater {
            type
            ctaLink {
              target
              title
              url
            }
          }
          paddingTop
          paddingBottom
          sectionImage {
            ...MediaItemFragment
          }
        }
      }
      ... on Resource_Flexiblecontent_Blocks_TextBlock {
        fieldGroupName
        paragraphs {
          backgroundColor
          content
          contentAlignment
          hasCtas
          hasHeadingIcon
          title
          sectionId
          ctaRepeater {
            type
            ctaLink {
              target
              title
              url
            }
          }
          headingIcon {
            ...MediaItemFragment
          }
        }
      }
      ... on Resource_Flexiblecontent_Blocks_List {
        backgroundColor
        fieldGroupName
        title
        sectionCopy
        variant
        itemRepeater {
          title
          price
          content
          backgroundColor
          buttonType
          link {
            target
            title
            url
          }
        }
      }
      ... on Resource_Flexiblecontent_Blocks_Quotes {
        fieldGroupName
        pawIconPosition
        quote
        subtext
        title
      }
      ... on Resource_Flexiblecontent_Blocks_PetCarousel {
        fieldGroupName
        title
        cta {
          target
          title
          url
        }
        buttonStyle
        pets {
          ... on Animal {
            id
            uri
            title
            content
            databaseId
            animalDetails {
              animalBirthday
              animalDescription
              animalIntakeDate
              animalName
              animalNid
              animalWeight
              animalUniqueId
              animalSex
              animalPhotoGallery {
                photo
              }
              animalCoverPhoto
            }
          }
          ... on Cat {
            id
            uri
            title
            content
            databaseId
            animalDetails {
              animalBirthday
              animalDescription
              animalIntakeDate
              animalName
              animalNid
              animalWeight
              animalUniqueId
              animalSex
              animalPhotoGallery {
                photo
              }
              animalCoverPhoto
            }
          }
        }
        variant
        arrowsPlacement
      }
      ... on Resource_Flexiblecontent_Blocks_PostsBlock {
        fieldGroupName
        backgroundColor
        hasPawIcon
        variant
        title
        pawIconPosition
        useLatestPosts
        postsGrid {
          ... on Post {
            id
            title
            uri
            excerpt
            featuredImage {
              node {
                ...MediaItemFragment
              }
            }
          }
          ... on Resource {
            id
            title
            uri
            excerpt
            featuredImage {
              node {
                ...MediaItemFragment
              }
            }
          }
          ... on Page {
            id
            title
            uri
            featuredImage {
              node {
                ...MediaItemFragment
              }
            }
          }
          ... on Event {
            id
            title
            uri
            excerpt
            featuredImage {
              node {
                ...MediaItemFragment
              }
            }
          }
        }
        postsStatic {
          ... on Post {
            id
            title
            uri
            excerpt
            featuredImage {
              node {
                ...MediaItemFragment
              }
            }
          }
          ... on Resource {
            id
            title
            uri
            excerpt
            featuredImage {
              node {
                ...MediaItemFragment
              }
            }
          }
          ... on Page {
            id
            title
            uri
            featuredImage {
              node {
                ...MediaItemFragment
              }
            }
          }
          ... on Event {
            id
            title
            uri
            excerpt
            featuredImage {
              node {
                ...MediaItemFragment
              }
            }
          }
        }
      }
      ... on Resource_Flexiblecontent_Blocks_Form {
        content
        fieldGroupName
        form
        title
        backgroundColor
        sectionId
      }
      ... on Resource_Flexiblecontent_Blocks_SplitText {
        variant
        hasPawIcon
        pawIconPosition
        title
        cardRepeater {
          title
          content
          linkType
          link {
            title
            url
            target
          }
        }
      }
      ... on Resource_Flexiblecontent_Blocks_Impact {
        fieldGroupName
        variant
        backgroundColor
        hasHeadingIcon
        title
        headingIcon {
          ...MediaItemFragment
        }
        impactItems {
          impactIcon {
            ...MediaItemFragment
          }
          impactNumber
          isDollarAmount
          subtext
          isAbbreviated
        }
      }
      ... on Resource_Flexiblecontent_Blocks_FeaturedCards {
        backgroundColor
        cardType
        dividerLines
        title
        hasHeadingIcon
        hasCtas
        cardRepeater {
          contentAligned
          cardBackgroundColor
          cardContent
          cardHasLink
          cardTitle
          hasCardIcon
          buttonType
          cardLink {
            target
            title
            url
          }
          cardIcon {
            ...MediaItemFragment
          }
        }
        ctaRepeater {
          type
          ctaLink {
            target
            title
            url
          }
        }
        headingIcon {
          ...MediaItemFragment
        }
      }
      ... on Resource_Flexiblecontent_Blocks_Accordion {
        backgroundColor
        fieldGroupName
        title
        sectionCopy
        hasHeadingIcon
        headingIcon {
          ...MediaItemFragment
        }
        itemRepeater {
          title
          itemContent
          hasCtas
          ctaRepeater {
            ctaLink {
              target
              title
              url
            }
          }
        }
      }
      ... on Resource_Flexiblecontent_Blocks_EventBlock {
        variant
        event {
          ... on Event {
            ...EventFeedFragment
          }
        }
        events {
          ... on Event {
            ...EventFeedFragment
          }
        }
      }

      ... on Resource_Flexiblecontent_Blocks_TeamMembers {
        fieldGroupName
        title
        pawsSide
        hasUnderline
        members {
          ... on Member {
            databaseId
            title
            teamMemberOptions {
              title
              bio
              headshot {
                ...MediaItemFragment
              }
              email {
                target
                title
                url
              }
            }
          }
        }
      }
    }
  }
  ${MEDIA_ITEM_FRAGMENT}
  ${EVENT_FEED_FRAGMENT}
`;
